"use client";

import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Sheet } from "@/components/shell/Sheet";
import { useAuth } from "@/lib/auth/context";
import {
  createIndividualMoneyGoal,
  createFamilyGoal,
  deleteGoal,
  updateIndividualMoneyGoal,
  updateFamilyGoal,
} from "@/lib/db/goals";
import { useHouseholdGoals } from "@/lib/hooks/useHouseholdGoals";
import { formatPLN, minorToZl, parsePLN } from "@/lib/utils/money";
import { goalDisplayByKind } from "@/lib/utils/goalDisplay";
import { toast } from "@/components/shell/Toaster";
import { isIndividualMoneyGoal, type Goal, type GoalKind } from "@/lib/schemas";

const EMOJI_OPTIONS_INDIVIDUAL = [
  "🎯", "🎮", "📱", "🚲", "🎧", "📚", "👟", "🧸", "🎨", "🎁",
];
const EMOJI_OPTIONS_FAMILY_COUNT = [
  "💛", "🤝", "🌟", "🎯", "🍕", "🏖️", "🎬", "🎉", "🎁", "🏕️",
];
const EMOJI_OPTIONS_FAMILY_OFFLINE = [
  "🌳", "🚲", "⚽", "🏞️", "🏕️", "🎒", "🧗", "🏊", "🚶", "🎨",
];

function emojisFor(kind: GoalKind): string[] {
  if (kind === "individual_money") return EMOJI_OPTIONS_INDIVIDUAL;
  if (kind === "family_count") return EMOJI_OPTIONS_FAMILY_COUNT;
  return EMOJI_OPTIONS_FAMILY_OFFLINE;
}

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * uid właściciela dla individual_money. Wymagany gdy tworzysz cel
   * indywidualny. Niewymagany dla family_*.
   */
  ownerUid?: string;
  /** Jeśli przekazany — edycja, inaczej tworzenie. */
  existingGoal?: Goal | null;
  /**
   * Wymuś konkretny kind od razu (np. "individual_money" gdy klikniesz
   * "+ Dodaj cel" obok dziecka). Pomija krok wyboru typu.
   */
  forcedKind?: GoalKind;
};

/**
 * Uniwersalny dialog tworzenia/edycji celu — trzy warianty (`kind`):
 *  - individual_money — kwotowy cel pieniężny (dziecko / parent)
 *  - family_count — wspólne zbieranie zadań pomocy 💛
 *  - family_offline_count — wspólne zbieranie zadań offline 🌳
 *
 * Krok 1 (tylko przy tworzeniu, gdy brak `forcedKind`/`existingGoal`):
 * wybór typu z 3 kart.
 *
 * Krok 2: formularz zależny od kind. Edycja pomija krok 1 (kind niezmienne).
 *
 * Limit: jeden aktywny cel rodzinny (family_*) na HH. Egzekwowane w UI
 * przez sprawdzenie istniejących household goals; jeśli istnieje aktywny
 * (bez completedAt), blokujemy create i pokazujemy toast.
 */
export function GoalDialog(props: Props) {
  // Mount-only kiedy `open=true` — gwarantuje świeży state hooków (pickedKind,
  // emoji, title, ...) za każdym razem gdy dialog się otwiera. Bez tego
  // poprzednie kliknięcia w step 1 zostawały w pickedKind po reopenie.
  if (!props.open) return null;
  return <GoalDialogInner {...props} />;
}

function GoalDialogInner({
  open,
  onClose,
  ownerUid,
  existingGoal,
  forcedKind,
}: Props) {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const isParent = userDoc?.role === "parent";
  const { goals: householdGoals } = useHouseholdGoals(hid);

  // Lokalny `pickedKind` używany **tylko** w trybie create gdy user wybierze
  // typ z karty (krok 1). Edycja i create-z-forcedKind biorą kind bezpośrednio
  // z propsów.
  const [pickedKind, setPickedKind] = useState<GoalKind | null>(null);

  // Wynikowy kind: edycja → existingGoal.kind, create → forcedKind / pickedKind.
  const kind: GoalKind | null =
    existingGoal?.kind ?? forcedKind ?? pickedKind;

  // Stan formularza (kroku 2). Reset gdy dialog się otwiera z innym celem.
  const dialogKey = `${open}-${existingGoal?.id ?? "new"}-${kind ?? "pick"}`;

  const initialEmoji = existingGoal?.emoji
    ?? (kind ? goalDisplayByKind(kind).emoji : "🎯");
  const initialTitle = existingGoal?.title ?? "";
  const initialTargetZl =
    existingGoal && isIndividualMoneyGoal(existingGoal)
      ? minorToZl(existingGoal.targetMinor)
      : "";
  const initialTargetCount =
    existingGoal && existingGoal.kind !== "individual_money"
      ? String(existingGoal.targetCount)
      : "";

  // Zasada 50/50 — istniejąca konfiguracja celu (jeśli edytujemy cel z dopłatą).
  const existingMatch =
    existingGoal &&
    isIndividualMoneyGoal(existingGoal) &&
    existingGoal.fullPriceMinor !== undefined
      ? {
          fullPriceMinor: existingGoal.fullPriceMinor,
          childSharePct: existingGoal.childSharePct ?? 50,
          matchedBy: existingGoal.matchedBy,
        }
      : null;

  const [emoji, setEmoji] = useState(initialEmoji);
  const [title, setTitle] = useState(initialTitle);
  const [targetZl, setTargetZl] = useState(initialTargetZl);
  const [targetCountStr, setTargetCountStr] = useState(initialTargetCount);
  const [matchEnabled, setMatchEnabled] = useState(existingMatch !== null);
  // Włączenie 50/50 chowa pole "Cel (zł)" i pokazuje "Cena rzeczy". Bez
  // prefilla wartość celu, którą dziecko już ustawiło, znikała. Traktujemy
  // istniejący cel jako cenę rzeczy — dziecko zbiera swoją część (cena × %).
  const [fullPriceZl, setFullPriceZl] = useState(
    existingMatch ? minorToZl(existingMatch.fullPriceMinor) : initialTargetZl,
  );
  const [childShare, setChildShare] = useState(
    existingMatch?.childSharePct ?? 50,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset pól nie jest potrzebny — komponent remountuje się przy każdym
  // otwarciu (early return null gdy !open w GoalDialog wrapperze), więc
  // useState'y dostają świeże initial values za każdym razem.

  const handlePickKind = (k: GoalKind) => {
    setPickedKind(k);
    // Domyślny emoji per kind, jeśli user nie wybrał własnego
    setEmoji(goalDisplayByKind(k).emoji);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!hid || !firebaseUser || !kind) return;
    setError(null);
    if (!title.trim()) {
      setError("Wpisz nazwę celu");
      return;
    }
    if (kind === "individual_money") {
      const owner = existingGoal && isIndividualMoneyGoal(existingGoal)
        ? existingGoal.userId
        : ownerUid;
      if (!owner) {
        setError("Brak właściciela celu");
        return;
      }

      // Dziecko edytujące własny cel 50/50: kwoty ustala rodzic — patch
      // ogranicza się do tytułu i emoji (rules i tak by odrzuciły resztę).
      const childEditsMatched = !isParent && existingMatch !== null;

      let targetMinor: number | undefined;
      let match:
        | { fullPriceMinor: number; childSharePct: number; matchedBy: string }
        | null
        | undefined;

      if (childEditsMatched) {
        targetMinor = undefined;
        match = undefined;
      } else if (isParent && matchEnabled) {
        const fullPriceMinor = parsePLN(fullPriceZl);
        if (!fullPriceMinor || fullPriceMinor < 200) {
          setError("Cena rzeczy: min. 2 zł");
          return;
        }
        targetMinor = Math.round((fullPriceMinor * childShare) / 100);
        match = {
          fullPriceMinor,
          childSharePct: childShare,
          matchedBy: existingMatch?.matchedBy ?? firebaseUser.uid,
        };
      } else {
        const parsed = parsePLN(targetZl);
        if (!parsed || parsed < 100) {
          setError("Cel min. 1 zł");
          return;
        }
        targetMinor = parsed;
        // Edycja celu, który miał 50/50, z wyłączonym toggle → zdejmij zasadę.
        match = existingMatch !== null ? null : undefined;
      }

      setBusy(true);
      try {
        if (existingGoal) {
          await updateIndividualMoneyGoal(hid, existingGoal.id, {
            title: title.trim(),
            emoji,
            ...(targetMinor !== undefined ? { targetMinor } : {}),
            ...(match !== undefined ? { match } : {}),
          });
          toast.success("Cel zaktualizowany");
        } else {
          await createIndividualMoneyGoal(hid, firebaseUser.uid, {
            userId: owner,
            title: title.trim(),
            emoji,
            targetMinor: targetMinor!,
            ...(match
              ? {
                  fullPriceMinor: match.fullPriceMinor,
                  childSharePct: match.childSharePct,
                  matchedBy: match.matchedBy,
                }
              : {}),
          });
          toast.success(match ? "Cel 50/50 ustawiony 🤝" : "Cel ustawiony 🎯");
        }
        onClose();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Błąd");
      } finally {
        setBusy(false);
      }
      return;
    }

    // family_count / family_offline_count
    if (!isParent) {
      setError("Cele rodziny tworzy rodzic");
      return;
    }
    const targetCount = Number.parseInt(targetCountStr, 10);
    if (!Number.isFinite(targetCount) || targetCount < 1 || targetCount > 1000) {
      setError("Liczba zadań: 1–1000");
      return;
    }
    if (!existingGoal) {
      const hasActiveFamily = householdGoals.some((g) => !g.completedAt);
      if (hasActiveFamily) {
        toast(
          "Macie już aktywny cel rodzinny — zakończ go lub usuń, żeby zacząć nowy",
        );
        return;
      }
    }
    setBusy(true);
    try {
      if (existingGoal) {
        await updateFamilyGoal(hid, existingGoal.id, {
          title: title.trim(),
          emoji,
          targetCount,
        });
        toast.success("Cel rodziny zaktualizowany");
      } else {
        await createFamilyGoal(hid, firebaseUser.uid, {
          kind,
          title: title.trim(),
          emoji,
          targetCount,
        });
        toast.success(
          kind === "family_count"
            ? "Cel rodziny ustawiony 💛"
            : "Cel rodziny ustawiony 🌳",
        );
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Błąd");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!hid || !existingGoal) return;
    if (!confirm("Usunąć cel?")) return;
    setBusy(true);
    try {
      await deleteGoal(hid, existingGoal.id);
      toast("Cel usunięty");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Błąd");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "mt-1 w-full h-11 rounded-[12px] border border-line bg-card px-3 text-sm focus:outline-none focus:border-accent";

  // Dialog header zależny od kroku/kind
  const headerTitle = existingGoal
    ? kind === "individual_money"
      ? "Edytuj cel"
      : "Edytuj cel rodziny"
    : kind === null
      ? "Nowy cel"
      : kind === "individual_money"
        ? "Nowy cel oszczędzania"
        : "Nowy cel rodziny";

  // KROK 1 — wybór typu. Pokazuje się tylko przy tworzeniu bez forcedKind.
  if (!existingGoal && kind === null) {
    return (
      <Sheet key={dialogKey} open={open} onClose={onClose} title={headerTitle}>
        <div className="space-y-3">
          <p className="text-sm text-muted">
            Co chcecie osiągnąć?
          </p>
          <KindCard
            kind="individual_money"
            title="Mój cel"
            emoji="💰"
            description="Odkładam ze swojego salda na coś dla mnie"
            onClick={() => handlePickKind("individual_money")}
            disabled={!ownerUid}
          />
          {isParent && (
            <>
              <KindCard
                kind="family_count"
                title="Cel rodziny — pomoc"
                emoji="💛"
                description="Razem zbieramy zadania pomocy rodzinie"
                onClick={() => handlePickKind("family_count")}
              />
              <KindCard
                kind="family_offline_count"
                title="Cel rodziny — aktywność poza ekranem"
                emoji="🌳"
                description="Razem zbieramy zadania poza ekranem"
                onClick={() => handlePickKind("family_offline_count")}
              />
            </>
          )}
          {!isParent && (
            <p className="text-[12px] text-muted leading-relaxed pt-1">
              Cele rodziny tworzy rodzic — porozmawiajcie razem.
            </p>
          )}
        </div>
      </Sheet>
    );
  }

  // KROK 2 — formularz. kind już znane.
  const k = kind!;
  const isFamily = k !== "individual_money";
  const meta = goalDisplayByKind(k);

  return (
    <Sheet key={dialogKey} open={open} onClose={onClose} title={headerTitle}>
      <div className="space-y-4">
        <div>
          <span className="text-xs text-muted">Ikona</span>
          <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1">
            {emojisFor(k).map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => setEmoji(e)}
                className={`h-11 w-11 rounded-[12px] text-2xl flex-shrink-0 transition-colors ${
                  emoji === e ? "bg-accent-soft ring-2 ring-accent" : "bg-soft"
                }`}
                aria-pressed={emoji === e}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="text-xs text-muted">
            {isFamily ? "Nazwa celu — co razem osiągniecie" : "Nazwa celu"}
          </span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={
              isFamily
                ? "np. weekend w górach, wieczór w pizzerii"
                : "np. Nowa gra"
            }
            className={inputCls}
            maxLength={60}
          />
          {isFamily && (
            <p className="text-[11px] text-muted mt-1 leading-relaxed">
              Aplikacja nie księguje nagrody — to wasza umowa.
            </p>
          )}
        </label>

        {k === "individual_money" ? (
          <div className="space-y-3">
            {!isParent && existingMatch !== null ? (
              <div className="rounded-[12px] bg-soft p-3 text-[12px] text-muted leading-relaxed">
                🤝 Cel z zasadą 50/50 — zbierasz{" "}
                <strong>
                  {formatPLN(
                    Math.round(
                      (existingMatch.fullPriceMinor *
                        existingMatch.childSharePct) /
                        100,
                    ),
                  )}
                </strong>{" "}
                z {formatPLN(existingMatch.fullPriceMinor)}, resztę dokłada
                rodzic przy zakupie. Kwoty ustala rodzic.
              </div>
            ) : (
              <>
                {isParent && (
                  <button
                    type="button"
                    onClick={() => setMatchEnabled((v) => !v)}
                    aria-pressed={matchEnabled}
                    className="w-full flex items-center justify-between gap-2 rounded-[12px] border border-line bg-card px-3 py-2.5 text-left"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">
                        🤝 Zasada 50/50
                      </span>
                      <span className="block text-[11px] text-muted mt-0.5 leading-relaxed">
                        Dziecko zbiera swoją część, Ty dokładasz resztę przy
                        zakupie
                      </span>
                    </span>
                    <span
                      className={`flex-shrink-0 text-[11px] font-bold rounded-full px-2.5 py-1 ${
                        matchEnabled
                          ? "bg-green text-white"
                          : "bg-soft text-muted"
                      }`}
                    >
                      {matchEnabled ? "Włączona" : "Wyłączona"}
                    </span>
                  </button>
                )}

                {isParent && matchEnabled ? (
                  <>
                    <label className="block">
                      <span className="text-xs text-muted">
                        Cena rzeczy (zł)
                      </span>
                      <input
                        value={fullPriceZl}
                        onChange={(e) => setFullPriceZl(e.target.value)}
                        inputMode="decimal"
                        placeholder="2000,00"
                        className={`${inputCls} font-num`}
                      />
                    </label>
                    <div>
                      <span className="text-xs text-muted">Część dziecka</span>
                      <div className="mt-1.5 flex gap-1.5">
                        {[30, 40, 50, 60].map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setChildShare(p)}
                            aria-pressed={childShare === p}
                            className={`h-9 flex-1 rounded-[12px] text-sm font-num font-semibold transition-colors ${
                              childShare === p
                                ? "bg-accent-soft ring-2 ring-accent"
                                : "bg-soft"
                            }`}
                          >
                            {p}%
                          </button>
                        ))}
                      </div>
                    </div>
                    {(() => {
                      const full = parsePLN(fullPriceZl);
                      if (!full || full < 200) return null;
                      const childPart = Math.round((full * childShare) / 100);
                      return (
                        <p className="text-[12px] text-muted leading-relaxed">
                          Dziecko zbiera <strong>{formatPLN(childPart)}</strong>
                          , Ty dokładasz{" "}
                          <strong>{formatPLN(full - childPart)}</strong> przy
                          zakupie.
                        </p>
                      );
                    })()}
                    <a
                      href="https://sianko.pl/blog/oszczedzanie-zasada-50-50"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block text-[12px] text-accent underline"
                    >
                      Jak działa zasada 50/50 →
                    </a>
                  </>
                ) : (
                  <label className="block">
                    <span className="text-xs text-muted">Cel (zł)</span>
                    <input
                      value={targetZl}
                      onChange={(e) => setTargetZl(e.target.value)}
                      inputMode="decimal"
                      placeholder="100,00"
                      className={`${inputCls} font-num`}
                    />
                  </label>
                )}
              </>
            )}
          </div>
        ) : (
          <label className="block">
            <span className="text-xs text-muted">
              Ile {meta.unitLabel} chcecie zebrać?
            </span>
            <input
              value={targetCountStr}
              onChange={(e) =>
                setTargetCountStr(e.target.value.replace(/[^0-9]/g, ""))
              }
              inputMode="numeric"
              placeholder={k === "family_count" ? "30" : "20"}
              className={`${inputCls} font-num`}
              maxLength={4}
            />
          </label>
        )}

        {error && <p className="text-sm text-danger">{error}</p>}

        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={busy}
          >
            Anuluj
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={handleSubmit}
            disabled={busy}
          >
            {busy ? "Zapisuję…" : existingGoal ? "Zapisz" : "Ustaw cel"}
          </Button>
        </div>

        {existingGoal && (
          <Button
            type="button"
            variant="ghost"
            block
            onClick={handleDelete}
            disabled={busy}
            className="text-danger"
          >
            <Trash2 className="h-4 w-4" />
            Usuń cel
          </Button>
        )}
      </div>
    </Sheet>
  );
}

function KindCard({
  title,
  emoji,
  description,
  onClick,
  disabled,
}: {
  kind: GoalKind;
  title: string;
  emoji: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-[16px] border-[1.5px] border-line bg-card p-4 text-left transition-colors hover:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="flex items-start gap-3">
        <span className="text-3xl flex-shrink-0" aria-hidden>
          {emoji}
        </span>
        <div className="min-w-0">
          <div className="font-display text-[16px] font-extrabold text-navy">
            {title}
          </div>
          <div className="text-[12px] text-muted mt-0.5 leading-relaxed">
            {description}
          </div>
        </div>
      </div>
    </button>
  );
}
